import Immutable from "immutable";
import { normalize } from "normalizr";
import { clientSchema } from "schemas/client";
import isEqual from "lodash.isequal";
import isURL from "validator/lib/isURL";
import keyConverter from "services/key-converter";
import { convertToUnitTime } from "services/time";
import {
  AdaMessengerPlatformRecord,
  ChatPlatformRecord,
  MessengerPlatformRecord,
  NuanceLAPlatformRecord,
  SalesforcePlatformRecord,
  SmsPlatformRecord,
  ZendeskAgentWorkspacePlatformRecord,
  ZendeskLAPlatformRecord,
  ZendeskPlatformRecord
} from "./models";

/**
 * @param {Platform} platform
 * @param {Record<string, unknown>} changed
 * @returns {number}
 */
function getCurrentZendeskStep(platform, changed = {}) {
  if ("enabled" in changed && Object.keys(changed).length === 1) {
    // if only enabled has changed, don't change the step number
    return platform.get("currentStep");
  }

  if (changed && (changed.auth || changed.accountKey) && platform.get("isDirty")) {
    // if anything in Step 1 has changed, need to go back to the start
    return 0;
  }

  if (
    platform.getIn(["record", "auth", "clientSecret"]) &&
    platform.getIn(["record", "auth", "clientId"]) &&
    platform.getIn(["record", "accountKey"])
  ) {
    if (platform.get("isDirty")) {
      if (platform.getIn(["record", "agentEmail"])) {
        // have all the information but haven't saved
        return 2;
      }

      // When we delete the agent email
      return 1;
    }

    if (platform.getIn(["record", "agentEmail"])) {
      // Here we are completely configured
      return 3;
    }

    return 1;
  }

  return 0;
}

/**
 * Validates the Salesforce Platform Record before an account can be linked
 * @param  {Record} platformRecord
 * @returns {Boolean} if valid
 */
export function validatePreSalesforcePlatform(platformRecord) {
  const validURLs = [
    ["salesforceHostnameUrl"],
    ["auth", "instanceUrl"],
    ["auth", "redirectUri"]
  ].map(path => isURL(
    platformRecord.getIn(path), { require_protocol: true }
  )).every(Boolean);

  const validOtherFields = [
    ["organizationId"],
    ["deploymentId"],
    ["auth", "clientId"],
    ["auth", "clientSecret"]
  ].map(path => platformRecord.getIn(path)).every(Boolean);

  return validURLs && validOtherFields;
}

/**
 * Validates the full Salesforce Platform Record
 * @param  {Record} platformRecord
 * @returns {Boolean} if valid
 */
export function validateSalesforcePlatform(platformRecord) {
  const validAccountFields = [
    ["auth", "email"],
    ["accessToken"],
    ["refreshToken"]
  ].map(path => platformRecord.getIn(path)).every(Boolean);

  return validatePreSalesforcePlatform(platformRecord) && validAccountFields;
}

/**
 * Updates the expiryTime and expiryUnitOfTime in Chat Platform
 * @param  {Record} platformRecord
 * @returns {Object} objects to update Platform
 */
function updateChatExpiryTime(platformRecord) {
  const results = convertToUnitTime(platformRecord.get("timeToExpiry"));

  if (results) {
    return {
      expiryUnitOfTime: results[0],
      expiryTime: results[1]
    };
  }

  return {};
}

/**
 * Get the platform map
 * @param {string} platformType platform name
 * @param {boolean} isPublished is the platform saved
 * @param {object} payload values to initialize platform record with
 * @param {boolean} isSaved required for the ZD 2-step creation process
 */
export function getPlatformMap(platformType, isPublished = false, payload, isSaved = false) {
  const base = Immutable.Map({
    record: getRecordType(platformType, payload),
    isLoading: false,
    isPublished,
    isDirty: false,
    isValid: platformType === "chat",
    // Need inSaved property for the ZD 2-step creation process
    isSaved
  });
  let platformMap = base;

  if (platformType === "zendesk_chat" || platformType === "zendesk_liveagent") {
    platformMap = base.merge({
      currentStep: getCurrentZendeskStep(base)
    });
  } else if (platformType === "salesforce_liveagent") {
    platformMap = base.merge({
      isFormComplete: validatePreSalesforcePlatform(base.get("record"))
    });
  } else if (platformType === "chat") {
    platformMap = base.mergeIn(
      ["record"],
      updateChatExpiryTime(base.get("record"))
    );
  }

  return platformMap;
}

/**
 * Retrieve and initialize the appropriate platform
 * @param {string} platformType The name of the platform
 * @param {Object} payload values to initialize the platform record
 */
function getRecordType(platformType, payload = null) {
  let record;

  switch (platformType) {
    case "sms":
      record = new SmsPlatformRecord(payload);
      break;
    case "messenger":
      record = new MessengerPlatformRecord(payload);
      break;
    case "zendesk_chat":
      record = new ZendeskPlatformRecord(payload);
      break;
    case "nuance_liveagent":
      record = new NuanceLAPlatformRecord(payload);
      break;
    case "zendesk_liveagent":
      record = new ZendeskLAPlatformRecord(payload);
      break;
    case "salesforce_liveagent":
      record = new SalesforcePlatformRecord(payload);
      break;
    case "ada_messenger":
      record = new AdaMessengerPlatformRecord(payload);
      break;

    case "zaw_liveagent": {
      record = new ZendeskAgentWorkspacePlatformRecord(payload);
      break;
    }

    case "chat":
    default:
      record = new ChatPlatformRecord(payload);
  }

  return record;
}

let normalizedData;
let lastSavedPlatforms = Immutable.Map();

const platformsMap = Immutable.Map({
  chat: getPlatformMap("chat"),
  sms: getPlatformMap("sms"),
  messenger: getPlatformMap("messenger"),
  zendesk_chat: getPlatformMap("zendesk_chat"),
  nuance_liveagent: getPlatformMap("nuance_liveagent"),
  zendesk_liveagent: getPlatformMap("zendesk_liveagent"),
  salesforce_liveagent: getPlatformMap("salesforce_liveagent"),
  ada_messenger: getPlatformMap("ada_messenger"),
  zaw_liveagent: getPlatformMap("zaw_liveagent")
});

export const platformsReducer = (state = platformsMap, action) => {
  let nextState = state;

  switch (action.type) {
    case "PLATFORM_POST_REQUEST":
    case "PLATFORM_UPDATE_REQUEST":
      return nextState.setIn([action.platform.getIn(["record", "name"]), "isLoading"], true);
    case "FETCH_ONE_CLIENT_SUCCESS": // TODO MEAS-470: Do not subscribe to another resource action
    case "PLATFORM_UPDATE_SUCCESS":
    case "PLATFORM_REFRESH_SUCCESS":

    // We shouldn't do fallthrough, but I can't do anything about this right now
    // eslint-disable-next-line no-fallthrough
    case "PLATFORM_POST_SUCCESS": {
      normalizedData = normalize(keyConverter(action.response.data.client), clientSchema);
      const { platforms } = normalizedData.entities;
      let platformsArray;

      if (platforms) {
        platformsArray = Object.keys(platforms).map(name => platforms[name]);

        platformsArray.forEach((platform) => {
          const objectToMerge = {};
          const payload = Immutable.fromJS(keyConverter(platform));
          objectToMerge[platform.name] = getPlatformMap(platform.name, true, payload, true);
          nextState = nextState.merge(objectToMerge).setIn([platform.name, "isLoading"], false);
          lastSavedPlatforms = nextState.merge(objectToMerge);
          nextState = setMetaData(nextState, platform.name, lastSavedPlatforms);
        });
      }

      return nextState;
    }

    case "PLATFORM_DELETE_SUCCESS":
      return nextState.setIn([action.platformName], getPlatformMap(action.platformName));
    case "PLATFORM_UPDATE_FAILURE":
    case "PLATFORM_POST_FAILURE":
      return nextState.setIn([action.platform.getIn(["record", "name"]), "isLoading"], false);
    case "UPDATE_PLATFORM":
      nextState = nextState.mergeDeepIn([action.platform, "record"], action.payload);
      nextState = setMetaData(nextState, action.platform, lastSavedPlatforms);

      if (action.metadata) {
        nextState = nextState.mergeIn([action.platform], action.metadata);
      }

      if (action.platform === "zendesk_chat") {
        nextState = nextState.setIn(
          [action.platform, "currentStep"],
          getCurrentZendeskStep(nextState.get("zendesk_chat"), action.payload)
        );
      } else if (action.platform === "zendesk_liveagent") {
        nextState = nextState.setIn(
          [action.platform, "currentStep"],
          getCurrentZendeskStep(nextState.get("zendesk_liveagent"), action.payload)
        );
      } else if (action.platform === "salesforce_liveagent") {
        nextState = nextState.setIn(
          [action.platform, "isFormComplete"],
          validatePreSalesforcePlatform(nextState.getIn(["salesforce_liveagent", "record"]))
        );
      }

      return nextState;
    case "PLATFORM_REFRESH_REQUEST":
    default:
      return nextState;
  }
};

/**
 * @param {Platforms} state
 * @param {string} platformType
 * @param {Platforms} newLastSavedPlatforms
 * @returns {Platforms}
 */
function setMetaData(state, platformType, newLastSavedPlatforms) {
  return state.mergeIn([platformType], {
    isDirty: checkIfDirty(state, platformType, newLastSavedPlatforms),
    isValid: checkIfValid(state, platformType)
  });
}

/**
 * @param {Platforms} state
 * @param {string} platformType
 * @param {Platforms} newLastSavedPlatforms
 * @returns {boolean}
 */
function checkIfDirty(state, platformType, newLastSavedPlatforms) {
  const currentRecord = state.getIn([platformType, "record"]).toJS();
  const lastSavedRecord = newLastSavedPlatforms.size && newLastSavedPlatforms.getIn([platformType, "record"]).toJS();

  return !isEqual(currentRecord, lastSavedRecord);
}

/**
 * Checks if platform fields are valid
 * @param {Object} state
 * @param {String} platformType
 */
function checkIfValid(state, platformType) {
  const platformRecord = state.get(platformType).get("record");
  let isValid = true;

  switch (platformType) {
    case "chat":
      if (platformRecord.rollout === null) {
        isValid = false;
      } else if (
        !isURL(state.getIn(["chat", "record", "privacyLink"]), { require_protocol: true }) &&
        !state.getIn(["chat", "record", "showLogo"]) &&
        state.getIn(["chat", "record", "privacyLink"])
      ) {
        isValid = false;
      }

      break;
    case "sms":
      if (!platformRecord.authId || !platformRecord.authKey) {
        isValid = false;
      }

      break;
    case "messenger":
      if (!platformRecord.accessToken) {
        isValid = false;
      }

      break;
    case "zendesk_chat":
      if (
        !platformRecord.agentEmail ||
        !platformRecord.getIn(["auth", "clientId"]) ||
        !platformRecord.getIn(["auth", "clientSecret"]) ||
        !platformRecord.getIn(["accountKey"])
      ) {
        isValid = false;
      }

      break;

    case "zaw_liveagent": {
      const {
        auth
      } = platformRecord;
      const validSuncoCreds = [
        auth.get("appId"),
        auth.get("apiKey"),
        auth.get("apiSecret")
      ].every(Boolean);
      const validZendeskCreds = [
        auth.get("subdomain"),
        auth.get("email"),
        auth.get("apiToken")
      ].every(Boolean);

      if (
        !validSuncoCreds || !validZendeskCreds
      ) {
        isValid = false;
      }

      break;
    }

    case "nuance_liveagent":
      if (
        !platformRecord.siteId ||
        !platformRecord.getIn(["auth", "clientId"]) ||
        !platformRecord.getIn(["auth", "clientSecret"]) ||
        !isURL(platformRecord.getIn(["auth", "redirectUri"]), { require_protocol: true })
      ) {
        isValid = false;
      }

      break;
    case "zendesk_liveagent":
      if (
        !platformRecord.agentEmail ||
        !platformRecord.getIn(["accountKey"]) ||
        !platformRecord.getIn(["auth", "clientId"]) ||
        !platformRecord.getIn(["auth", "clientSecret"]) ||
        !isURL(platformRecord.getIn(["auth", "redirectUri"]), { require_protocol: true })
      ) {
        isValid = false;
      }

      break;

    case "salesforce_liveagent":
      isValid = validateSalesforcePlatform(platformRecord);

      break;

    default:
      break;
  }

  return isValid;
}
